import { useMemo, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { AnimatePresence, motion } from "framer-motion";
import { api } from "@convex/_generated/api";
import type { Doc, Id } from "@convex/_generated/dataModel";
import {
  addDays,
  formatMinute,
  isValidDate,
  occursOn,
  parseMinute,
} from "@convex/lib/time";
import {
  cardClass,
  errorMessage,
  inputClass,
  primaryButtonClass,
  secondaryButtonClass,
} from "@/lib/ui";

type Kind = "cancel" | "move" | "extra";

const KIND_LABEL: Record<Kind, string> = {
  cancel: "Cancel a lesson",
  move: "Move a lesson",
  extra: "Add a one-off lesson",
};

function todayIso(): string {
  const now = new Date();
  return [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, "0"),
    String(now.getDate()).padStart(2, "0"),
  ].join("-");
}

export default function ExceptionsPanel() {
  const today = todayIso();
  // Recent past stays visible: a cancellation you entered last week is still
  // the explanation for a missing recording.
  const exceptions = useQuery(api.schedule.listExceptions, {
    from: addDays(today, -30),
  });
  const rules = useQuery(api.schedule.listRules, {});
  const subjects = useQuery(api.subjects.list, { includeArchived: true });
  const [creating, setCreating] = useState(false);

  const subjectsById = useMemo(() => {
    const map = new Map<Id<"subjects">, Doc<"subjects">>();
    for (const subject of subjects ?? []) {
      map.set(subject._id, subject);
    }
    return map;
  }, [subjects]);

  const rulesById = useMemo(() => {
    const map = new Map<Id<"scheduleRules">, Doc<"scheduleRules">>();
    for (const rule of rules ?? []) {
      map.set(rule._id, rule);
    }
    return map;
  }, [rules]);

  if (exceptions === undefined || rules === undefined || subjects === undefined) {
    return <p className="text-sm text-neutral-500">Loading…</p>;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
        <p className="text-sm text-neutral-500">
          One-off changes to the timetable. Showing the last 30 days onward.
        </p>
        {!creating && (
          <motion.button
            type="button"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            className={`${secondaryButtonClass} self-start sm:self-auto shrink-0`}
            onClick={() => setCreating(true)}
          >
            New exception
          </motion.button>
        )}
      </div>

      <AnimatePresence>
        {creating && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <ExceptionForm
              rules={rules}
              subjects={subjects.filter((subject) => !subject.archived)}
              subjectsById={subjectsById}
              onDone={() => setCreating(false)}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {exceptions.length === 0 && !creating && (
        <p className={`${cardClass} text-sm text-neutral-500`}>
          No exceptions. The timetable runs as scheduled.
        </p>
      )}

      <div className="flex flex-col gap-2">
        <AnimatePresence initial={false}>
          {exceptions.map((exception) => (
            <motion.div
              key={exception._id}
              layout
              initial={{ opacity: 0, y: 3 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.98 }}
            >
              <ExceptionRow
                exception={exception}
                rule={
                  exception.ruleId === undefined
                    ? undefined
                    : rulesById.get(exception.ruleId)
                }
                subjectsById={subjectsById}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}

function ExceptionRow({
  exception,
  rule,
  subjectsById,
}: {
  exception: Doc<"scheduleExceptions">;
  rule: Doc<"scheduleRules"> | undefined;
  subjectsById: Map<Id<"subjects">, Doc<"subjects">>;
}) {
  const remove = useMutation(api.schedule.removeException);
  const [error, setError] = useState<string | null>(null);

  const subjectId = exception.subjectId ?? rule?.subjectId;
  const subject = subjectId === undefined ? undefined : subjectsById.get(subjectId);

  const description = (() => {
    if (exception.kind === "cancel") {
      return rule === undefined
        ? "cancelled"
        : `cancelled - was ${formatMinute(rule.startMinute)}–${formatMinute(rule.endMinute)}`;
    }
    if (exception.kind === "extra") {
      return `one-off ${formatMinute(exception.newStartMinute ?? 0)}–${formatMinute(
        exception.newEndMinute ?? 0,
      )}`;
    }
    const parts: string[] = [];
    if (exception.newDate !== undefined) parts.push(`to ${exception.newDate}`);
    if (exception.newStartMinute !== undefined && exception.newEndMinute !== undefined) {
      parts.push(
        `at ${formatMinute(exception.newStartMinute)}–${formatMinute(exception.newEndMinute)}`,
      );
    }
    return `moved ${parts.join(" ")}`;
  })();

  return (
    <div className={`${cardClass} flex flex-wrap items-center gap-3`}>
      <span className="font-mono text-sm">{exception.date}</span>
      <span
        aria-hidden
        className="h-3 w-3 shrink-0 rounded-full"
        style={{ backgroundColor: subject?.color ?? "#9CA3AF" }}
      />
      <span className="font-medium">{subject?.name ?? "Unknown subject"}</span>
      <span className="text-sm text-neutral-600 dark:text-neutral-400">
        {description}
      </span>
      {exception.note !== undefined && (
        <span className="text-xs text-neutral-500">{exception.note}</span>
      )}

      <div className="w-full sm:w-auto sm:ml-auto flex items-center justify-end pt-2 sm:pt-0 border-t sm:border-t-0 border-neutral-100 dark:border-neutral-800/80">
        <button
          type="button"
          className={secondaryButtonClass}
          onClick={() => {
            setError(null);
            void remove({ id: exception._id }).catch((caught: unknown) => {
              setError(errorMessage(caught, "Could not delete the exception."));
            });
          }}
        >
          Delete
        </button>
      </div>

      {error !== null && (
        <p className="w-full text-sm text-red-600 dark:text-red-400">{error}</p>
      )}
    </div>
  );
}

function ExceptionForm({
  rules,
  subjects,
  subjectsById,
  onDone,
}: {
  rules: Doc<"scheduleRules">[];
  subjects: Doc<"subjects">[];
  subjectsById: Map<Id<"subjects">, Doc<"subjects">>;
  onDone: () => void;
}) {
  const create = useMutation(api.schedule.createException);
  const [kind, setKind] = useState<Kind>("cancel");
  const [date, setDate] = useState(todayIso());
  const [ruleId, setRuleId] = useState<string>("");
  const [subjectId, setSubjectId] = useState<string>(subjects[0]?._id ?? "");
  const [newDate, setNewDate] = useState("");
  const [start, setStart] = useState("09:00");
  const [end, setEnd] = useState("10:30");
  // Until the times are touched, a move keeps the occurrence's own times, so
  // moving only the date does not silently reschedule the hour as well.
  const [timeTouched, setTimeTouched] = useState(false);
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Only occurrences that actually exist on the chosen date can be changed -
  // the same check the mutation runs.
  const occurrences = useMemo(() => {
    if (!isValidDate(date)) {
      return [];
    }
    return rules.filter((rule) => rule.active && occursOn(rule, date));
  }, [rules, date]);

  const selectedRuleId =
    occurrences.some((rule) => rule._id === ruleId)
      ? ruleId
      : (occurrences[0]?._id ?? "");
  const selectedRule = occurrences.find((rule) => rule._id === selectedRuleId);

  const inheritTimes = kind === "move" && !timeTouched && selectedRule !== undefined;
  const startValue = inheritTimes ? formatMinute(selectedRule.startMinute) : start;
  const endValue = inheritTimes ? formatMinute(selectedRule.endMinute) : end;

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);

    const startMinute = parseMinute(startValue);
    const endMinute = parseMinute(endValue);
    if (kind !== "cancel" && (startMinute === null || endMinute === null)) {
      setError("Times must be in HH:MM form.");
      return;
    }

    setSaving(true);
    try {
      if (kind === "extra") {
        await create({
          date,
          kind: "extra",
          subjectId: subjectId as Id<"subjects">,
          newStartMinute: startMinute ?? undefined,
          newEndMinute: endMinute ?? undefined,
          note,
        });
      } else if (kind === "cancel") {
        if (selectedRuleId === "") {
          setError("Pick the lesson to cancel.");
          return;
        }
        await create({
          date,
          kind: "cancel",
          ruleId: selectedRuleId as Id<"scheduleRules">,
          note,
        });
      } else {
        if (selectedRuleId === "") {
          setError("Pick the lesson to move.");
          return;
        }
        const movesDate = newDate !== "";
        await create({
          date,
          kind: "move",
          ruleId: selectedRuleId as Id<"scheduleRules">,
          newDate: movesDate ? newDate : undefined,
          newStartMinute: startMinute ?? undefined,
          newEndMinute: endMinute ?? undefined,
          note,
        });
      }
      onDone();
    } catch (caught) {
      setError(errorMessage(caught, "Could not save the exception."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className={`${cardClass} flex flex-col gap-3`}>
      <div className="flex flex-wrap gap-3">
        <label className="flex w-52 flex-col gap-1 text-sm">
          <span className="text-neutral-600 dark:text-neutral-400">What</span>
          <select
            className={inputClass}
            value={kind}
            onChange={(e) => setKind(e.target.value as Kind)}
          >
            {(Object.keys(KIND_LABEL) as Kind[]).map((value) => (
              <option key={value} value={value}>
                {KIND_LABEL[value]}
              </option>
            ))}
          </select>
        </label>

        <label className="flex w-full sm:w-44 flex-col gap-1 text-sm">
          <span className="text-neutral-600 dark:text-neutral-400">Date</span>
          <input
            className={inputClass}
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </label>

        {kind === "extra" ? (
          <label className="flex w-full sm:min-w-56 sm:flex-1 flex-col gap-1 text-sm">
            <span className="text-neutral-600 dark:text-neutral-400">Subject</span>
            <select
              className={inputClass}
              value={subjectId}
              onChange={(e) => setSubjectId(e.target.value)}
            >
              {subjects.map((subject) => (
                <option key={subject._id} value={subject._id}>
                  {subject.name}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <label className="flex w-full sm:min-w-56 sm:flex-1 flex-col gap-1 text-sm">
            <span className="text-neutral-600 dark:text-neutral-400">Lesson</span>
            <select
              className={inputClass}
              value={selectedRuleId}
              onChange={(e) => setRuleId(e.target.value)}
              disabled={occurrences.length === 0}
            >
              {occurrences.length === 0 && (
                <option value="">No lessons scheduled that day</option>
              )}
              {occurrences.map((rule) => (
                <option key={rule._id} value={rule._id}>
                  {subjectsById.get(rule.subjectId)?.name ?? "Unknown subject"} ·{" "}
                  {formatMinute(rule.startMinute)}–{formatMinute(rule.endMinute)}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {kind !== "cancel" && (
        <div className="flex flex-wrap gap-3">
          {kind === "move" && (
            <label className="flex w-full sm:w-44 flex-col gap-1 text-sm">
              <span className="text-neutral-600 dark:text-neutral-400">
                New date - optional
              </span>
              <input
                className={inputClass}
                type="date"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
              />
            </label>
          )}
          <div className="flex w-full sm:w-auto gap-3">
            <label className="flex flex-1 sm:w-28 flex-col gap-1 text-sm">
              <span className="text-neutral-600 dark:text-neutral-400">Start</span>
              <input
                className={inputClass}
                type="time"
                value={startValue}
                onChange={(e) => {
                  setTimeTouched(true);
                  setStart(e.target.value);
                }}
              />
            </label>
            <label className="flex flex-1 sm:w-28 flex-col gap-1 text-sm">
              <span className="text-neutral-600 dark:text-neutral-400">End</span>
              <input
                className={inputClass}
                type="time"
                value={endValue}
                onChange={(e) => {
                  setTimeTouched(true);
                  setEnd(e.target.value);
                }}
              />
            </label>
          </div>
        </div>
      )}

      <label className="flex flex-col gap-1 text-sm">
        <span className="text-neutral-600 dark:text-neutral-400">Note</span>
        <input
          className={inputClass}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="optional - public holiday, lecturer away, …"
        />
      </label>

      {error !== null && (
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      )}

      <div className="flex items-center gap-2">
        <button
          type="submit"
          className={primaryButtonClass}
          disabled={saving || (kind === "extra" && subjects.length === 0)}
        >
          Add exception
        </button>
        <button type="button" className={secondaryButtonClass} onClick={onDone}>
          Cancel
        </button>
      </div>
    </form>
  );
}
